// src/websocket/socket.ts
import EventEmitter from 'eventemitter3';
import { Auth } from '../auth/auth';
import { Constant } from '../config/constant';
import { Subscription } from './subscription';
import { Interception } from './interception';
import { LongPollClient } from './http_poll';
import { subscribe_to_channel } from './helper_functions';
import { LiveObjSubscription } from './live_object_subscription';
export default class Socket extends EventEmitter {
    constructor(encrypt, decrypt, lpClient) {
        super();
        this.subscriptions = new Map();
        this.interceptors = new Map();
        this.connection = null;
        this.isConnectionActive = false;
        this.heartbeatInterval = null;
        this.pendingSendMessges = [];
        this.secureCallbacks = {};
        this.pendingIncomingMessages = new Map();
        // protected art_secure_line: any;
        this.pullSource = 'socket';
        this.pushSource = 'socket';
        this.isConnecting = false;
        this.isReConnecting = false;
        this.autoReconnect = false;
        this.handleConnectionBinding = (data) => {
            this.setAutoReconnect(true);
            data = JSON.parse(data);
            this.connection = {
                ConnectionId: data["connection_id"],
                InstanceId: data["instance_id"],
                TenantName: this.credentials["OrgTitle"],
                Environment: this.credentials["Environment"],
                ProjectKey: this.credentials["ProjectKey"],
            };
            console.log('Live connection opened', this.connection.ConnectionId);
            this.emit('connection', this.connection);
            this.isConnectionActive = true;
            this.startHeartbeat();
            if (this.autoReconnect) {
                this.subscriptions.forEach((subscriptionInstance, subscription) => {
                    subscriptionInstance.reconnect();
                });
                this.interceptors.forEach((interceptorInstance, interceptor) => {
                    interceptorInstance.reconnect();
                });
            }
        };
        this.encrypt = encrypt;
        this.decrypt = decrypt;
        this.lpClient = lpClient;
    }
    static getInstance(encrypt, decrypt) {
        if (!Socket.instance) {
            const lpClient = new LongPollClient({
                endpoint: Constant.LPOLL,
                getAuthHeaders: async () => {
                    const auth = Auth.getInstance();
                    await auth.authenticate();
                    const { AccessToken } = auth.getAuthData();
                    const { OrgTitle, Environment, ProjectKey } = auth.getCredentials();
                    return {
                        'Authorization': `Bearer ${AccessToken}`,
                        'X-Org': OrgTitle,
                        'Environment': Environment,
                        'ProjectKey': ProjectKey,
                    };
                },
                onMessages: msgs => { Socket.instance.processIncomingMessages(msgs); },
                onError: err => console.error('LP error:', err),
            });
            Socket.instance = new Socket(encrypt, decrypt, lpClient);
        }
        return Socket.instance;
    }
    async initiateSocket(credentials) {
        var _a;
        if (this.websocket && this.isConnectionActive)
            return;
        this.credentials = credentials;
        if (typeof WebSocket !== 'undefined') {
            try {
                await this.connectWebSocket();
                this.pullSource = 'socket';
                this.pushSource = 'socket';
                return;
            }
            catch (wsErr) {
                console.warn('WebSocket failed, falling back to SSE:', wsErr);
            }
        }
        if (typeof EventSource !== 'undefined') {
            try {
                await this.connectSSE();
                this.pullSource = 'sse';
                this.pushSource = 'http'; // SSE is pull only
                return;
            }
            catch (sseErr) {
                console.warn('SSE failed, falling back to HTTP poll:', sseErr);
            }
        }
        this.pullSource = 'http';
        this.pushSource = 'http';
        this.lpClient.start((_a = this.connection) === null || _a === void 0 ? void 0 : _a.ConnectionId);
    }
    async connectWebSocket() {
        var _a, _b;
        try {
            if (this.isConnecting)
                return;
            this.isConnecting = true;
            // Get the Auth singleton instance
            const auth = Auth.getInstance(this.credentials);
            let authData;
            try {
                authData = await auth.authenticate(this.isReConnecting);
            }
            catch (err) {
                console.error("Authentication failed:", err);
                this.isConnecting = false;
                this.emit('close', { type: "error" });
                return; // don't proceed if auth fails
            }
            const params = new URLSearchParams({
                connection_id: (_b = (_a = this.connection) === null || _a === void 0 ? void 0 : _a.ConnectionId) !== null && _b !== void 0 ? _b : "",
                "Org-Title": this.credentials.OrgTitle,
                token: authData.AccessToken,
                environment: this.credentials.Environment,
                "project-key": this.credentials.ProjectKey,
            });
            // Note: URLSearchParams will handle encoding for you
            const wsUrl = new URL(Constant.WS_URL);
            wsUrl.search = params.toString();
            // And finally:
            const fullWsUrl = wsUrl.toString();
            await this.safeClose();
            await new Promise((resolve, reject) => {
                const ws = new WebSocket(fullWsUrl);
                let timedOut = false;
                const timer = setTimeout(() => {
                    timedOut = true;
                    ws.close();
                    reject(new Error('WebSocket handshake timeout'));
                }, 5000);
                // Setup event handlers
                ws.onopen = (event) => {
                    clearTimeout(timer);
                    if (timedOut)
                        return;
                    this.isConnecting = false;
                    this.emit('open', event);
                    this.websocket = ws;
                    resolve();
                };
                ws.onmessage = (event) => {
                    this.parseIncomingMessage(event.data);
                };
                ws.onerror = (event) => {
                    if (timedOut)
                        return;
                    clearTimeout(timer);
                    console.error('Live error:', JSON.stringify(event));
                    // this.emit('error', event);
                    this.isConnecting = false;
                    this.isConnectionActive = false;
                    this.emit('close', { type: "error" });
                    reject(new Error('WebSocket connection error'));
                };
                ws.onclose = (event) => {
                    if (timedOut)
                        return;
                    clearTimeout(timer);
                    console.log('Live closed, code=', event.code, 'reason=', event.reason);
                    this.isConnecting = false;
                    this.isConnectionActive = false;
                    this.emit('close', event);
                    reject(new Error(`WebSocket closed with code ${event.code}`));
                };
            }).catch(err => {
                this.isConnecting = false;
                this.isConnectionActive = false;
                this.emit('close', { type: 'error', message: err.message });
                throw err; // so your fallbacks kick in
            });
        }
        catch (e) {
            console.log("No server present");
            this.isConnecting = false;
            this.isConnectionActive = false;
            this.emit('close', { type: "error" });
            return;
        }
    }
    async safeClose(timeout = 1000) {
        const ws = this.websocket;
        if (!ws)
            return;
        if (ws.readyState === WebSocket.CLOSED)
            return;
        await new Promise(resolve => {
            const onClose = () => {
                ws.removeEventListener('close', onClose);
                clearTimeout(timer);
                this.websocket = undefined;
                resolve();
            };
            const timer = setTimeout(() => {
                ws.removeEventListener('close', onClose);
                resolve();
            }, timeout);
            ws.addEventListener('close', onClose);
            try {
                ws.close();
            }
            catch (_a) { }
        });
    }
    async connectSSE() {
        // Get the Auth singleton instance
        const auth = Auth.getInstance(this.credentials);
        let authData;
        try {
            authData = await auth.authenticate();
        }
        catch (err) {
            console.error("Authentication failed:", err);
            this.emit('close', { type: "error" });
            return; // don't proceed if auth fails
        }
        // 1) Build a plain object where every value is a string
        const params = {
            "Org-Title": encodeURIComponent(this.credentials.OrgTitle),
            token: authData.AccessToken,
            environment: encodeURIComponent(this.credentials.Environment),
            "project-key": encodeURIComponent(this.credentials.ProjectKey),
        };
        const qs = new URLSearchParams(params).toString();
        const url = `${Constant.SSE_URL}?${qs}`;
        const es = new EventSource(url);
        es.onopen = () => {
            this.emit("open", new Event("open"));
            this.isConnectionActive = true;
            // this.listenConfiguration();
        };
        es.onerror = (err) => {
            console.error("SSE error:", err);
            this.emit("error", err);
        };
        es.addEventListener("close", (e) => {
            console.log("SSE server requested close, shutting down client EventSource.");
            this.isConnectionActive = false;
            es.close();
            // optionally, set a flag to prevent reconnect logic if you have any
        });
        es.onmessage = (e) => {
            // e.data is the raw JSON or array payload
            this.parseIncomingMessage(e.data);
        };
    }
    async pushForSecureLine(event, data, listen = false) {
        return new Promise((resolve, reject) => {
            var _a, _b, _c;
            const refId = `${(_a = this.connection) === null || _a === void 0 ? void 0 : _a.ConnectionId}_secure_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
            const message = {
                from: (_c = (_b = this.connection) === null || _b === void 0 ? void 0 : _b.ConnectionId) !== null && _c !== void 0 ? _c : "",
                channel: "art_secure",
                event: event,
                content: JSON.stringify(data !== null && data !== void 0 ? data : {}),
                ref_id: refId
            };
            if (listen) {
                this.secureCallbacks[`secure-${refId}`] = resolve;
            }
            else {
                resolve(null);
            }
            this.sendMessage(JSON.stringify(message));
        });
    }
    // public async listenForSecureLine(event: string, validationFn?: (data: any) => boolean): Promise<any> {
    //     if (!this.art_secure_line) {
    //         await this.wait()
    //     }
    //     // Return a promise that resolves when ecr_key data is received
    //     return new Promise((resolve, reject) => {
    //         const onEcrKey = (data: any) => {
    //             // Resolve the promise with the received data
    //             let valid = true;
    //             if (validationFn) {
    //                 valid = validationFn?.(data);
    //             }
    //             if (valid) {
    //                 resolve(data);
    //                 // Remove the listener after resolving
    //                 this.art_secure_line.off(event, onEcrKey);
    //             }
    //         };
    //         // Add the listener for ecr_key
    //         this.art_secure_line.on(event, onEcrKey);
    //         // Optionally, add a timeout to reject if no response is received
    //         setTimeout(() => {
    //             // Reject the promise with an error
    //             reject(new Error(`${event} response timeout`));
    //             // Ensure the listener is also removed on timeout
    //             this.art_secure_line.off(event, onEcrKey);
    //         }, 20000);
    //     });
    // }
    removeSubscription(channel) {
        this.subscriptions.delete(channel);
    }
    /**
       * Subscribe to a specific channel.
       * @param channel The channel name to subscribe to.
       * @returns A Subscription instance for the specified channel.
       */
    async subscribe(channel) {
        return this.handleSubscription(channel);
    }
    async handleSubscription(channel) {
        var _a, _b;
        await this.wait();
        const connectionId = (_b = (_a = this.connection) === null || _a === void 0 ? void 0 : _a.ConnectionId) !== null && _b !== void 0 ? _b : "";
        // Check if a subscription for the channel already exists
        if (this.subscriptions.has(channel)) {
            const sub = this.subscriptions.get(channel);
            // if ((sub as any).connectionId !== connectionId) {
            //     console.warn(
            //         `[ART] Rebinding subscription ${channel} due to connection change`,
            //         (sub as any).connectionId,
            //         "→",
            //         connectionId
            //     );
            //     // cleanup old subscription
            //     sub.unsubscribe?.();
            //     this.subscriptions.delete(channel);
            // } else {
            // same connection → just resubscribe
            sub.subscribe();
            return sub;
            // }
        }
        const channelConfig = await this.validateSubscription(channel, "subscribe");
        if (!channelConfig) {
            return Promise.reject(new Error(`Channel ${channel} not found`));
        }
        let subscription;
        if (channelConfig.channelType === "shared-object") {
            subscription = new LiveObjSubscription(connectionId, channelConfig, this, "subscribe");
        }
        else {
            console.log("channel config:>>>>", channelConfig);
            subscription = new Subscription(connectionId, channelConfig, this, "subscribe");
        }
        this.subscriptions.set(channel, subscription);
        const buf = this.pendingIncomingMessages.get(channel);
        if (buf) {
            for (const { event, payload } of buf) {
                subscription.handleMessage(event, payload);
            }
            this.pendingIncomingMessages.delete(channel);
        }
        return subscription;
    }
    async validateSubscription(channelName, process) {
        return new Promise(async (resolve, reject) => {
            if (["art_config", "art_secure"].includes(channelName)) {
                return resolve({
                    channelName: channelName,
                    channelNamespace: "",
                    channelType: "default",
                    presenceUsers: [],
                    snapshot: null,
                    subscriptionID: "",
                    orchestratorEnabled: false
                });
            }
            await subscribe_to_channel(channelName, process, this).then((config) => {
                resolve(config);
            }).catch((error) => {
                reject(error);
            });
        });
    }
    getConnection() {
        return this.connection;
    }
    /**
       * Register interceptor.
       * @param interceptor The interceptor name to register to.
       * @returns A Interception instance.
       */
    async intercept(interceptor, fn) {
        return new Promise(async (resolve, reject) => {
            await this.wait();
            if (this.interceptors.has(interceptor)) {
                resolve(this.interceptors.get(interceptor));
                return;
            }
            // Create a new Subscription instance
            const interception = new Interception(interceptor, fn, this);
            await interception.validateInterception().then((config) => {
                this.interceptors.set(interceptor, interception);
                resolve(interception);
            }).catch((error) => {
                reject(error);
            });
        });
    }
    parseIncomingMessage(message) {
        try {
            const parsed = JSON.parse(message);
            if (Array.isArray(parsed)) {
                this.processIncomingMessages(parsed);
                return;
            }
            else {
                this.handleIncomingMessage(parsed);
            }
        }
        catch (error) {
            console.error('Failed to parse incoming message:', error);
        }
    }
    processIncomingMessages(messages) {
        messages.forEach(msg => this.handleIncomingMessage(msg));
    }
    /**
     * Handle incoming WebSocket messages and route them to the appropriate Subscription.
     * @param message The raw message received from the WebSocket.
     */
    handleIncomingMessage(parsedMessage) {
        try {
            const { channel, namespace, ref_id, event, return_flag, interceptor_name, content: data } = parsedMessage;
            if (channel == "art_ready" && event == "ready") {
                this.handleConnectionBinding(data);
                return;
            }
            else if (channel == "art_secure") {
                const key = `secure-${ref_id}`;
                if (this.secureCallbacks.hasOwnProperty(key)) {
                    let parsedData = JSON.parse(data);
                    parsedData = {
                        channel,
                        namespace,
                        data: parsedData,
                        ref_id,
                        event
                    };
                    this.secureCallbacks[key](parsedData);
                    delete this.secureCallbacks[key];
                }
                return;
            }
            // Limit block
            if (!channel && (parsedMessage.code === 'CONNECTION_MINUTES_LIMIT_EXCEEDED' || parsedMessage.code === 'CONCURRENT_LIMIT_EXCEEDED')) {
                console.error(`[ART] ${parsedMessage.code === 'CONCURRENT_LIMIT_EXCEEDED' ? 'Concurrent connection limit reached' : 'Connection minutes limit exceeded'}. Reconnection blocked.`);
                this.emit('limitExceeded', { code: parsedMessage.code, error: parsedMessage.error });
                return;
            }
            if (!channel || (!event && return_flag != "SA")) {
                console.warn('Received message without channel or event:', parsedMessage);
                return;
            }
            if (event === 'shift_to_http') {
                // switch over to HTTP polling
                this.switchToHttpPoll();
                return;
            }
            if (event === 'error') {
                console.error('Received error message:', parsedMessage);
                // return
            }
            if (interceptor_name) {
                const interception = this.interceptors.get(interceptor_name);
                if (interception) {
                    delete parsedMessage["content"];
                    parsedMessage['data'] = data;
                    interception.handleMessage(channel, parsedMessage);
                }
                else {
                    console.warn(`No Interception found for channel: ${channel}`);
                }
            }
            else {
                let subscriptionKey = channel;
                if (namespace) {
                    subscriptionKey += ":" + namespace;
                }
                const subscription = this.subscriptions.get(subscriptionKey);
                delete parsedMessage["content"];
                parsedMessage['data'] = data;
                if (subscription) {
                    if (subscription instanceof Subscription || subscription instanceof LiveObjSubscription) {
                        subscription.handleMessage(event, parsedMessage);
                    }
                }
                else {
                    console.warn(`No subscription found for channel: ${subscriptionKey}, adding to buffer`);
                    const arr = this.pendingIncomingMessages.get(subscriptionKey) || [];
                    arr.push({ event, payload: parsedMessage });
                    this.pendingIncomingMessages.set(subscriptionKey, arr);
                }
            }
        }
        catch (error) {
            console.error('Failed to parse incoming message:', error);
        }
    }
    switchToHttpPoll() {
        var _a, _b;
        if (this.pullSource === 'http')
            return; // already polling
        console.info('🔄 Shifting pullSource → http, pushSource → http');
        this.pullSource = 'http';
        this.pushSource = 'http';
        // start long-poller (it will reuse connection_id you already have)
        this.lpClient.start((_b = (_a = this.connection) === null || _a === void 0 ? void 0 : _a.ConnectionId) !== null && _b !== void 0 ? _b : "");
    }
    /**
     * Send a message through the WebSocket.
     * @param message The message to send.
     */
    sendMessage(message) {
        if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
            this.websocket.send(message);
            return true;
        }
        else {
            this.pendingSendMessges.push(message);
            const error = new Error('WebSocket is not open. Cannot send message.');
            console.warn(error, message);
            this.emit('error', error);
            return false;
        }
    }
    setAutoReconnect(autoReconnect) {
        this.autoReconnect = autoReconnect;
    }
    async closeWebSocket(clearConnection = false) {
        await this.safeClose();
        this.isConnectionActive = false;
        this.connection = null;
        this.isConnecting = false;
        if (clearConnection) {
            this.pendingIncomingMessages.clear();
            this.pendingSendMessges = [];
            this.subscriptions.clear();
            this.interceptors.clear();
        }
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }
    }
    wait() {
        if (this.isConnectionActive)
            return Promise.resolve();
        return new Promise(resolve => {
            const onConn = () => {
                this.off('connection', onConn);
                resolve();
            };
            this.on('connection', onConn);
        });
    }
    _runHeartBeatPayload() {
        var _a;
        const subscriptions = [];
        this.subscriptions.forEach((sub, key) => {
            var _a;
            subscriptions.push({
                name: key,
                presenceTracking: (_a = sub.isListening) !== null && _a !== void 0 ? _a : false
            });
        });
        return {
            connectionId: ((_a = this.connection) === null || _a === void 0 ? void 0 : _a.ConnectionId) || null,
            timestamp: Date.now(),
            subscriptions
        };
    }
    startHeartbeat() {
        if (this.heartbeatInterval)
            return; // prevent multiple timers
        this.heartbeatInterval = setInterval(() => {
            if (!this.isConnectionActive)
                return; // only when alive
            const hbPayload = this._runHeartBeatPayload();
            this.pushForSecureLine("heartbeat", hbPayload, false);
        }, 30000);
    }
}
